// ─────────────────────────────────────────────────────────────────────────────
// Dashboard formatters, mirror the legacy wiring's number/date helpers so the
// React build renders the same strings the live dashboard does (fmt, _fmtBigShort,
// relative-date labels, short due labels). Pure functions, no markup.
// ─────────────────────────────────────────────────────────────────────────────

/** Thousands-separated integer (mirrors dashboard-wiring.js fmt()). */
export function fmt(n: number | null | undefined): string {
  if (n == null || !isFinite(Number(n))) return '-';
  return Number(n).toLocaleString('en-US');
}

/**
 * Short big-number label like "20.0K" / "1.92k" / "110.3k" used in the release
 * stats + the playlists subhead. Mirrors _fmtBigShort / the release-card "k"
 * formatting (label-recent-release-wiring.js fmtShort).
 */
export function fmtShort(n: number | null | undefined): string {
  if (n == null || !isFinite(Number(n))) return '-';
  const v = Number(n);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(Math.round(v));
}

/** Big-number short with an uppercase K (the playlists "20.0K total reach"). */
export function fmtReach(n: number | null | undefined): string {
  if (n == null || !isFinite(Number(n))) return '-';
  const v = Number(n);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return String(Math.round(v));
}

/** A signed percentage like "+1.2%" / "−0.6%" (mirrors p.delta.toFixed(1)). */
export function pctLabel(delta: number | null): string {
  if (delta == null) return '-';
  const sign = delta >= 0 ? '+' : '−';
  return sign + Math.abs(delta).toFixed(1) + '%';
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Parse an ISO date safely (noon to dodge TZ rollover, like the wiring). */
function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10) + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

/** "May 8, 2026", the release date label (toLocaleDateString in the wiring). */
export function longDate(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return 'No date set';
  return MONTHS_LONG[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
}

/** "Aug 7", the upcoming-pill date (short month + day). */
export function shortDate(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '';
  return MONTHS_SHORT[d.getMonth()] + ' ' + d.getDate();
}

/** "May 29", the to-do due label (mirrors shortDueLabel). */
export const shortDueLabel = shortDate;

/** "released 1 month ago" / "released 3 days ago" (mirrors relativeDateLabel). */
export function relativeReleaseLabel(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 0) {
    const ahead = Math.abs(days);
    return 'drops in ' + ahead + ' day' + (ahead === 1 ? '' : 's');
  }
  if (days === 0) return 'released today';
  if (days < 30) return 'released ' + days + ' day' + (days === 1 ? '' : 's') + ' ago';
  const months = Math.floor(days / 30);
  if (months < 12) return 'released ' + months + ' month' + (months === 1 ? '' : 's') + ' ago';
  const years = Math.floor(months / 12);
  return 'released ' + years + ' year' + (years === 1 ? '' : 's') + ' ago';
}

/** Day-of-week abbreviation for the Upcoming card date block ("Sat"). */
export function dowShort(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '';
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}

/** Two-digit day-of-month for the Upcoming card date block ("27"). */
export function dayOfMonth(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '';
  return String(d.getDate()).padStart(2, '0');
}

/** "14d ago" age stamp for playlist placements (from _days_since_added). */
export function daysAgoLabel(days: number | null | undefined): string {
  if (days == null || !isFinite(Number(days))) return '';
  const n = Math.max(0, Math.round(Number(days)));
  return n + 'd ago';
}

/** Initials from a person's name ("Drew Smyser" → "DS"). */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}
